<script setup>
import { inject } from 'vue'
import { useSystemBannerStore } from '@/stores/systemBanner'
import { useI18n } from '@/i18n'

const bannerStore = useSystemBannerStore()
const activeLang  = inject('activeLang', null)
const { t } = useI18n()

const severityMap = {
  danger: { icon: 'pi pi-exclamation-triangle', cls: 'banner--danger' },
  warn:   { icon: 'pi pi-exclamation-circle',   cls: 'banner--warn'   },
  info:   { icon: 'pi pi-info-circle',           cls: 'banner--info'   },
  success:{ icon: 'pi pi-check-circle',          cls: 'banner--success' },
}

function meta(type) {
  return severityMap[type] ?? severityMap.info
}

function message(banner) {
  const lang = activeLang?.value ?? 'en'
  return lang === 'de' ? (banner.messageDe ?? banner.messageEn) : (banner.messageEn ?? banner.messageDe)
}
</script>

<template>
  <div v-if="bannerStore.banners.length" class="system-banners">
    <div
      v-for="b in bannerStore.banners"
      :key="b.id"
      class="system-banner"
      :class="meta(b.type).cls"
    >
      <i :class="meta(b.type).icon" class="banner-icon" />
      <span class="banner-msg">{{ message(b) }}</span>
      <button
        v-if="b.dismissable"
        class="banner-close"
        @click="bannerStore.removeBanner(b.id)"
        :aria-label="t('common.close')"
      >
        <i class="pi pi-times" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.system-banners {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.system-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  font-size: 0.875rem;
  font-weight: 500;
  border-bottom: 1px solid transparent;
}

.banner-icon { font-size: 1rem; flex-shrink: 0; }

.banner-msg  { flex: 1; }

.banner-close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  opacity: 0.7;
  color: inherit;
  flex-shrink: 0;
}
.banner-close:hover { opacity: 1; background: rgba(0,0,0,.08); }

/* ── Severity variants ─────────────────────────────────────── */
.banner--danger  {
  background: color-mix(in srgb, var(--p-red-500, #ef4444) 14%, transparent);
  color: var(--p-red-700, #b91c1c);
  border-bottom-color: color-mix(in srgb, var(--p-red-500, #ef4444) 30%, transparent);
}
.banner--warn {
  background: color-mix(in srgb, var(--p-amber-500, #f59e0b) 14%, transparent);
  color: var(--p-amber-700, #b45309);
  border-bottom-color: color-mix(in srgb, var(--p-amber-500, #f59e0b) 30%, transparent);
}
.banner--info {
  background: color-mix(in srgb, var(--p-blue-500, #3b82f6) 12%, transparent);
  color: var(--p-blue-700, #1d4ed8);
  border-bottom-color: color-mix(in srgb, var(--p-blue-500, #3b82f6) 25%, transparent);
}
.banner--success {
  background: color-mix(in srgb, var(--p-green-500, #22c55e) 12%, transparent);
  color: var(--p-green-700, #15803d);
  border-bottom-color: color-mix(in srgb, var(--p-green-500, #22c55e) 25%, transparent);
}

/* Dark mode — Text etwas heller damit lesbar */
:global(.my-app-dark) .banner--danger  { color: var(--p-red-300,   #fca5a5); }
:global(.my-app-dark) .banner--warn    { color: var(--p-amber-300,  #fcd34d); }
:global(.my-app-dark) .banner--info    { color: var(--p-blue-300,   #93c5fd); }
:global(.my-app-dark) .banner--success { color: var(--p-green-300,  #86efac); }
</style>
